import { useState, useMemo } from 'react'
import { motion, AnimatePresence, Reorder } from 'framer-motion'
import {
  UserGroupIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  SparklesIcon,
  FunnelIcon,
  ChartBarIcon,
  ClockIcon,
  TagIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { Modal } from '../components/ui/Modal'
import { generateCRMSuggestion } from '../lib/groq'
import { clsx } from 'clsx'

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

const STAGES = [
  { id: 'lead', name: 'Leads', color: 'bg-metallic-100', textColor: 'text-metallic-600', dotColor: 'bg-metallic-400' },
  { id: 'prospect', name: 'Prospects', color: 'bg-blue-100', textColor: 'text-blue-600', dotColor: 'bg-blue-500' },
  { id: 'qualified', name: 'Qualified', color: 'bg-indigo-100', textColor: 'text-indigo-600', dotColor: 'bg-indigo-500' },
  { id: 'proposal', name: 'Proposal', color: 'bg-purple-100', textColor: 'text-purple-600', dotColor: 'bg-purple-500' },
  { id: 'negotiation', name: 'Negotiation', color: 'bg-warning-50', textColor: 'text-warning-600', dotColor: 'bg-warning-500' },
  { id: 'closed_won', name: 'Closed Won', color: 'bg-success-50', textColor: 'text-success-600', dotColor: 'bg-success-500' },
  { id: 'closed_lost', name: 'Closed Lost', color: 'bg-error-50', textColor: 'text-error-600', dotColor: 'bg-error-500' },
]

const STAGE_BADGE_VARIANT: Record<string, 'info' | 'primary' | 'success' | 'warning' | 'error'> = {
  lead: 'info',
  prospect: 'primary',
  qualified: 'primary',
  proposal: 'primary',
  negotiation: 'warning',
  closed_won: 'success',
  closed_lost: 'error',
}

interface Activity {
  id: number
  type: 'call' | 'email' | 'meeting' | 'note' | 'stage_change'
  description: string
  date: string
  icon: typeof ClockIcon
}

interface CRMContact {
  id: number
  company: string
  contactPerson: string
  email: string
  phone: string
  value: number
  stage: string
  lastActivity: string
  tags: string[]
  notes: string
  createdAt: string
  avatar: string
  activities: Activity[]
  stageHistory: { stage: string; date: string }[]
}

const contacts: CRMContact[] = [
  {
    id: 1,
    company: 'TechCorp Inc.',
    contactPerson: 'John Smith',
    email: 'john@techcorp.com',
    phone: '+1 (415) 555-0192',
    value: 125000,
    stage: 'proposal',
    lastActivity: '2 hours ago',
    tags: ['Enterprise', 'SaaS', 'Q2 Target'],
    notes: 'Very interested in the enterprise tier. Needs SOC2 compliance docs. Decision by end of quarter.',
    createdAt: '2026-01-15',
    avatar: 'JS',
    activities: [
      { id: 1, type: 'email', description: 'Sent revised proposal with custom pricing tiers', date: '2 hours ago', icon: EnvelopeIcon },
      { id: 2, type: 'call', description: 'Discussed integration requirements with their engineering lead', date: 'Yesterday', icon: PhoneIcon },
      { id: 3, type: 'meeting', description: 'Product demo — 8 attendees from TechCorp team', date: '3 days ago', icon: CalendarDaysIcon },
      { id: 4, type: 'stage_change', description: 'Moved from Qualified to Proposal', date: '3 days ago', icon: ArrowTrendingUpIcon },
      { id: 5, type: 'note', description: 'Budget approved for Q3. CFO is the final approver.', date: '1 week ago', icon: ClockIcon },
    ],
    stageHistory: [
      { stage: 'lead', date: 'Jan 15, 2026' },
      { stage: 'prospect', date: 'Feb 3, 2026' },
      { stage: 'qualified', date: 'Mar 10, 2026' },
      { stage: 'proposal', date: 'Apr 18, 2026' },
    ],
  },
  {
    id: 2,
    company: 'Digital Solutions Ltd.',
    contactPerson: 'Sarah Johnson',
    email: 'sarah@digitalsolutions.com',
    phone: '+1 (212) 555-0347',
    value: 89000,
    stage: 'negotiation',
    lastActivity: '1 hour ago',
    tags: ['Mid-Market', 'Renewal', 'Upsell'],
    notes: 'Current customer looking to upgrade from Starter to Pro plan. Negotiating a multi-year deal.',
    createdAt: '2025-09-20',
    avatar: 'SJ',
    activities: [
      { id: 1, type: 'call', description: 'Price negotiation call — they want 15% annual discount', date: '1 hour ago', icon: PhoneIcon },
      { id: 2, type: 'email', description: 'Sent contract draft with revised terms', date: 'Yesterday', icon: EnvelopeIcon },
      { id: 3, type: 'note', description: 'Competitor offered them 20% off. Need to match or provide better value.', date: '2 days ago', icon: ClockIcon },
    ],
    stageHistory: [
      { stage: 'lead', date: 'Sep 20, 2025' },
      { stage: 'qualified', date: 'Oct 5, 2025' },
      { stage: 'proposal', date: 'Nov 12, 2025' },
      { stage: 'negotiation', date: 'Apr 10, 2026' },
    ],
  },
  {
    id: 3,
    company: 'Global Enterprises Group',
    contactPerson: 'Mike Chen',
    email: 'mike.chen@globalent.com',
    phone: '+44 20 7946 0958',
    value: 340000,
    stage: 'qualified',
    lastActivity: '5 hours ago',
    tags: ['Enterprise', 'International', 'Strategic'],
    notes: 'Fortune 500 company. Long sales cycle expected. Engaging their CTO directly.',
    createdAt: '2026-02-01',
    avatar: 'MC',
    activities: [
      { id: 1, type: 'meeting', description: 'Technical deep-dive with CTO and VP Engineering', date: '5 hours ago', icon: CalendarDaysIcon },
      { id: 2, type: 'email', description: 'Follow-up with security questionnaire', date: 'Yesterday', icon: EnvelopeIcon },
      { id: 3, type: 'call', description: 'Initial discovery call — identified 3 pain points', date: '1 week ago', icon: PhoneIcon },
    ],
    stageHistory: [
      { stage: 'lead', date: 'Feb 1, 2026' },
      { stage: 'prospect', date: 'Feb 20, 2026' },
      { stage: 'qualified', date: 'Mar 28, 2026' },
    ],
  },
  {
    id: 4,
    company: 'StartUp Labs',
    contactPerson: 'Emily Davis',
    email: 'emily@startuplabs.io',
    phone: '+1 (650) 555-0178',
    value: 18500,
    stage: 'lead',
    lastActivity: '2 days ago',
    tags: ['Startup', 'Series A', 'Product-Led'],
    notes: 'Signed up for free trial. Showed strong engagement metrics. Needs nurturing.',
    createdAt: '2026-04-10',
    avatar: 'ED',
    activities: [
      { id: 1, type: 'email', description: 'Automated welcome sequence triggered', date: '2 days ago', icon: EnvelopeIcon },
      { id: 2, type: 'note', description: 'Used trial 14 times in first 3 days — high intent signal', date: '3 days ago', icon: ClockIcon },
    ],
    stageHistory: [
      { stage: 'lead', date: 'Apr 10, 2026' },
    ],
  },
  {
    id: 5,
    company: 'InnovateTech Corp',
    contactPerson: 'David Wilson',
    email: 'david@innovatetech.com',
    phone: '+1 (512) 555-0293',
    value: 67500,
    stage: 'proposal',
    lastActivity: '1 day ago',
    tags: ['Mid-Market', 'AI/ML', 'Custom'],
    notes: 'Needs custom AI integration module. Proposed a phased rollout plan.',
    createdAt: '2025-11-05',
    avatar: 'DW',
    activities: [
      { id: 1, type: 'email', description: 'Delivered custom proposal with phased pricing', date: '1 day ago', icon: EnvelopeIcon },
      { id: 2, type: 'meeting', description: 'Architecture review session with their dev team', date: '4 days ago', icon: CalendarDaysIcon },
      { id: 3, type: 'stage_change', description: 'Moved from Prospect to Proposal', date: '4 days ago', icon: ArrowTrendingUpIcon },
    ],
    stageHistory: [
      { stage: 'lead', date: 'Nov 5, 2025' },
      { stage: 'prospect', date: 'Dec 1, 2025' },
      { stage: 'qualified', date: 'Feb 15, 2026' },
      { stage: 'proposal', date: 'Apr 14, 2026' },
    ],
  },
  {
    id: 6,
    company: 'Nexus Partners',
    contactPerson: 'Lisa Park',
    email: 'lisa@nexuspartners.co',
    phone: '+1 (206) 555-0412',
    value: 92000,
    stage: 'closed_won',
    lastActivity: '3 days ago',
    tags: ['Enterprise', 'Partner', 'Multi-Year'],
    notes: 'Signed 3-year contract. Onboarding scheduled for May 1st. Champion is their COO.',
    createdAt: '2025-08-12',
    avatar: 'LP',
    activities: [
      { id: 1, type: 'note', description: 'Contract signed! 3-year deal at $92K/year.', date: '3 days ago', icon: ClockIcon },
      { id: 2, type: 'stage_change', description: 'Moved from Negotiation to Closed Won', date: '3 days ago', icon: ArrowTrendingUpIcon },
      { id: 3, type: 'meeting', description: 'Final signing meeting with legal teams', date: '4 days ago', icon: CalendarDaysIcon },
    ],
    stageHistory: [
      { stage: 'lead', date: 'Aug 12, 2025' },
      { stage: 'prospect', date: 'Sep 8, 2025' },
      { stage: 'qualified', date: 'Oct 22, 2025' },
      { stage: 'proposal', date: 'Dec 10, 2025' },
      { stage: 'negotiation', date: 'Jan 18, 2026' },
      { stage: 'closed_won', date: 'Apr 19, 2026' },
    ],
  },
  {
    id: 7,
    company: 'Bright Future Media',
    contactPerson: 'Alex Rivera',
    email: 'alex@brightfuture.media',
    phone: '+1 (310) 555-0687',
    value: 34000,
    stage: 'closed_lost',
    lastActivity: '1 week ago',
    tags: ['Media', 'Small Business', 'Price-Sensitive'],
    notes: 'Chose a cheaper competitor. Could revisit in 6 months when their budget refreshes.',
    createdAt: '2025-12-03',
    avatar: 'AR',
    activities: [
      { id: 1, type: 'note', description: 'Lost to CompetitorX — they undercut by 30%', date: '1 week ago', icon: ClockIcon },
      { id: 2, type: 'stage_change', description: 'Moved from Proposal to Closed Lost', date: '1 week ago', icon: ArrowTrendingUpIcon },
    ],
    stageHistory: [
      { stage: 'lead', date: 'Dec 3, 2025' },
      { stage: 'qualified', date: 'Jan 10, 2026' },
      { stage: 'proposal', date: 'Feb 20, 2026' },
      { stage: 'closed_lost', date: 'Apr 15, 2026' },
    ],
  },
  {
    id: 8,
    company: 'Atlas Robotics',
    contactPerson: 'Priya Sharma',
    email: 'priya@atlasrobotics.io',
    phone: '+1 (617) 555-0821',
    value: 156000,
    stage: 'qualified',
    lastActivity: '30 minutes ago',
    tags: ['Enterprise', 'Manufacturing', 'IoT'],
    notes: 'Building autonomous warehouse systems. Need edge computing integration. Very technical buyer.',
    createdAt: '2026-03-14',
    avatar: 'PS',
    activities: [
      { id: 1, type: 'call', description: 'Technical requirements call with engineering VP', date: '30 minutes ago', icon: PhoneIcon },
      { id: 2, type: 'email', description: 'Sent API documentation and sandbox access', date: 'Yesterday', icon: EnvelopeIcon },
      { id: 3, type: 'meeting', description: 'Facility tour and demo at their Boston HQ', date: '3 days ago', icon: CalendarDaysIcon },
    ],
    stageHistory: [
      { stage: 'lead', date: 'Mar 14, 2026' },
      { stage: 'prospect', date: 'Mar 28, 2026' },
      { stage: 'qualified', date: 'Apr 12, 2026' },
    ],
  },
]

type SortField = 'company' | 'contactPerson' | 'value' | 'stage' | 'lastActivity'
type SortDir = 'asc' | 'desc'

export const CRM = () => {
  const [view, setView] = useState<'pipeline' | 'list'>('pipeline')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [selectedContact, setSelectedContact] = useState<CRMContact | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [stageFilter, setStageFilter] = useState('all')
  const [tagFilter, setTagFilter] = useState<string[]>([])
  const [sortField, setSortField] = useState<SortField>('value')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [aiSuggestion, setAiSuggestion] = useState<{ emailDraft: string; closeScore: number; nextAction: string } | null>(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiModalOpen, setAiModalOpen] = useState(false)
  const [detailTab, setDetailTab] = useState<'timeline' | 'notes' | 'history'>('timeline')
  const [showFilters, setShowFilters] = useState(false)

  const [newContact, setNewContact] = useState({
    company: '',
    contactPerson: '',
    email: '',
    phone: '',
    value: '',
    stage: 'lead',
    tags: '',
    notes: '',
  })

  const allTags = useMemo(() => {
    const tagSet = new Set<string>()
    contacts.forEach((c) => c.tags.forEach((t) => tagSet.add(t)))
    return Array.from(tagSet)
  }, [])

  const filteredContacts = useMemo(() => {
    let result = contacts

    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        (c) =>
          c.company.toLowerCase().includes(q) ||
          c.contactPerson.toLowerCase().includes(q) ||
          c.email.toLowerCase().includes(q) ||
          c.tags.some((t) => t.toLowerCase().includes(q))
      )
    }

    if (stageFilter !== 'all') {
      result = result.filter((c) => c.stage === stageFilter)
    }

    if (tagFilter.length > 0) {
      result = result.filter((c) => tagFilter.some((t) => c.tags.includes(t)))
    }

    result = [...result].sort((a, b) => {
      let cmp = 0
      if (sortField === 'value') cmp = a.value - b.value
      else if (sortField === 'company') cmp = a.company.localeCompare(b.company)
      else if (sortField === 'contactPerson') cmp = a.contactPerson.localeCompare(b.contactPerson)
      else if (sortField === 'stage') cmp = STAGES.findIndex((s) => s.id === a.stage) - STAGES.findIndex((s) => s.id === b.stage)
      else cmp = 0
      return sortDir === 'asc' ? cmp : -cmp
    })

    return result
  }, [searchQuery, stageFilter, tagFilter, sortField, sortDir])

  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    STAGES.forEach((s) => (counts[s.id] = 0))
    filteredContacts.forEach((c) => (counts[c.stage] = (counts[c.stage] || 0) + 1))
    return counts
  }, [filteredContacts])

  const stats = useMemo(() => {
    const totalPipeline = contacts.reduce((sum, c) => sum + c.value, 0)
    const wonDeals = contacts.filter((c) => c.stage === 'closed_won')
    const lostDeals = contacts.filter((c) => c.stage === 'closed_lost')
    const closedTotal = wonDeals.length + lostDeals.length
    const conversionRate = closedTotal > 0 ? Math.round((wonDeals.length / closedTotal) * 100) : 0
    const avgDealSize = closedTotal > 0 ? Math.round(wonDeals.reduce((s, c) => s + c.value, 0) / wonDeals.length) : 0
    const wonThisMonth = wonDeals.length

    return { totalPipeline, conversionRate, avgDealSize, wonThisMonth }
  }, [])

  const handleAISuggest = async (contact: CRMContact) => {
    setAiLoading(true)
    setAiModalOpen(true)
    setAiSuggestion(null)
    try {
      const data = await generateCRMSuggestion(
        `Company: ${contact.company}, Contact: ${contact.contactPerson}, Email: ${contact.email}, Phone: ${contact.phone}, Deal Value: $${contact.value}, Stage: ${contact.stage}, Tags: ${contact.tags.join(', ')}, Last Activity: ${contact.lastActivity}, Notes: ${contact.notes}`
      )
      setAiSuggestion(data)
    } catch {
      setAiSuggestion({
        emailDraft: `Hi ${contact.contactPerson},\n\nI wanted to follow up on our recent discussions regarding ${contact.company}'s engagement with IntelCore. I believe there's a strong opportunity for us to help streamline your operations.\n\nWould you be available for a brief call this week to explore next steps?\n\nBest regards`,
        closeScore: 55,
        nextAction: 'Schedule a discovery call to understand specific pain points and demonstrate ROI.',
      })
    } finally {
      setAiLoading(false)
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDir('asc')
    }
  }

  const openDetail = (contact: CRMContact) => {
    setSelectedContact(contact)
    setIsDetailModalOpen(true)
    setDetailTab('timeline')
  }

  const toggleTagFilter = (tag: string) => {
    setTagFilter((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]))
  }

  const getStageName = (stageId: string) => STAGES.find((s) => s.id === stageId)?.name || stageId

  const handleCreateContact = () => {
    setIsCreateModalOpen(false)
    setNewContact({ company: '', contactPerson: '', email: '', phone: '', value: '', stage: 'lead', tags: '', notes: '' })
  }

  return (
    <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
      {/* Header */}
      <motion.div variants={item} className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-metallic-900">CRM</h1>
          <p className="text-metallic-500 mt-1">Manage your customer relationships and sales pipeline.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-metallic-100 rounded-lg p-1">
            <button
              onClick={() => setView('pipeline')}
              className={clsx('px-4 py-2 text-sm font-medium rounded-md transition-colors', view === 'pipeline' ? 'bg-white text-metallic-900 shadow-sm' : 'text-metallic-500 hover:text-metallic-700')}
            >
              Pipeline
            </button>
            <button
              onClick={() => setView('list')}
              className={clsx('px-4 py-2 text-sm font-medium rounded-md transition-colors', view === 'list' ? 'bg-white text-metallic-900 shadow-sm' : 'text-metallic-500 hover:text-metallic-700')}
            >
              List
            </button>
          </div>
          <Button leftIcon={<PlusIcon className="w-5 h-5" />} onClick={() => setIsCreateModalOpen(true)}>
            Add Contact
          </Button>
        </div>
      </motion.div>

      {/* Dashboard Stats */}
      <motion.div variants={item} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="gradient" padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
              <ChartBarIcon className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-metallic-500">Total Pipeline</p>
              <p className="text-xl font-bold text-metallic-900">${stats.totalPipeline.toLocaleString()}</p>
            </div>
          </div>
        </Card>
        <Card variant="gradient" padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-success-50 flex items-center justify-center">
              <ArrowTrendingUpIcon className="w-6 h-6 text-success-500" />
            </div>
            <div>
              <p className="text-sm text-metallic-500">Conversion Rate</p>
              <p className="text-xl font-bold text-metallic-900">{stats.conversionRate}%</p>
            </div>
          </div>
        </Card>
        <Card variant="gradient" padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <SparklesIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-metallic-500">Avg Deal Size</p>
              <p className="text-xl font-bold text-metallic-900">${stats.avgDealSize.toLocaleString()}</p>
            </div>
          </div>
        </Card>
        <Card variant="gradient" padding="sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-warning-50 flex items-center justify-center">
              <UserGroupIcon className="w-6 h-6 text-warning-500" />
            </div>
            <div>
              <p className="text-sm text-metallic-500">Won This Month</p>
              <p className="text-xl font-bold text-metallic-900">{stats.wonThisMonth}</p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Search & Filter Bar */}
      <motion.div variants={item}>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <Input
              placeholder="Search contacts, companies, tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
              className="flex-1"
            />
            <Button
              variant={showFilters ? 'primary' : 'secondary'}
              leftIcon={<FunnelIcon className="w-5 h-5" />}
              onClick={() => setShowFilters((v) => !v)}
            >
              Filters
              {(stageFilter !== 'all' || tagFilter.length > 0) && (
                <Badge variant="success" size="sm" className="ml-2">
                  {stageFilter !== 'all' ? 1 : 0} + {tagFilter.length}
                </Badge>
              )}
            </Button>
          </div>
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-4 mt-4 border-t border-metallic-100 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-metallic-600 mb-2">Stage</p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setStageFilter('all')}
                        className={clsx(
                          'px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                          stageFilter === 'all' ? 'bg-primary-500 text-white shadow-sm' : 'bg-metallic-100 text-metallic-600 hover:bg-metallic-200'
                        )}
                      >
                        All
                      </button>
                      {STAGES.map((s) => (
                        <button
                          key={s.id}
                          onClick={() => setStageFilter(s.id)}
                          className={clsx(
                            'px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
                            stageFilter === s.id ? 'bg-primary-500 text-white shadow-sm' : 'bg-metallic-100 text-metallic-600 hover:bg-metallic-200'
                          )}
                        >
                          {s.name} ({stageCounts[s.id]})
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-metallic-600 mb-2">Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {allTags.map((tag) => (
                        <button
                          key={tag}
                          onClick={() => toggleTagFilter(tag)}
                          className={clsx(
                            'px-3 py-1.5 rounded-lg text-sm font-medium transition-all inline-flex items-center gap-1',
                            tagFilter.includes(tag) ? 'bg-primary-500 text-white shadow-sm' : 'bg-metallic-100 text-metallic-600 hover:bg-metallic-200'
                          )}
                        >
                          <TagIcon className="w-3.5 h-3.5" />
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                  {(stageFilter !== 'all' || tagFilter.length > 0) && (
                    <button
                      onClick={() => {
                        setStageFilter('all')
                        setTagFilter([])
                      }}
                      className="text-sm text-error-500 hover:text-error-600 font-medium"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Card>
      </motion.div>

      {/* Pipeline View */}
      {view === 'pipeline' && (
        <motion.div variants={item}>
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-thin">
            {STAGES.map((stage) => {
              const stageContacts = filteredContacts.filter((c) => c.stage === stage.id)
              return (
                <div key={stage.id} className="flex-shrink-0 w-80">
                  <div className="flex items-center justify-between mb-3 px-1">
                    <div className="flex items-center gap-2">
                      <div className={clsx('w-2.5 h-2.5 rounded-full', stage.dotColor)} />
                      <h3 className="font-semibold text-metallic-900 text-sm">{stage.name}</h3>
                    </div>
                    <Badge variant={STAGE_BADGE_VARIANT[stage.id]}>{stageContacts.length}</Badge>
                  </div>
                  <Reorder.Group axis="y" values={stageContacts} onReorder={() => {}} className="space-y-3 min-h-[120px]">
                    {stageContacts.map((contact) => (
                      <Reorder.Item key={contact.id} value={contact}>
                        <motion.div layout layoutId={`card-${contact.id}`}>
                          <Card hover padding="sm" className="group" onClick={() => openDetail(contact)}>
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center text-xs font-bold text-primary-700 flex-shrink-0">
                                  {contact.avatar}
                                </div>
                                <div className="min-w-0">
                                  <h4 className="font-medium text-metallic-900 text-sm truncate">{contact.company}</h4>
                                  <p className="text-xs text-metallic-500 truncate">{contact.contactPerson}</p>
                                </div>
                              </div>
                              <span className="text-xs text-metallic-400 whitespace-nowrap ml-2">{contact.lastActivity}</span>
                            </div>
                            <div className="flex items-center justify-between mt-3">
                              <span className="text-lg font-bold text-primary-600">${contact.value.toLocaleString()}</span>
                            </div>
                            {contact.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {contact.tags.slice(0, 2).map((tag) => (
                                  <Badge key={tag} variant="info" size="sm">
                                    {tag}
                                  </Badge>
                                ))}
                                {contact.tags.length > 2 && (
                                  <Badge variant="info" size="sm">+{contact.tags.length - 2}</Badge>
                                )}
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex-1"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  openDetail(contact)
                                }}
                              >
                                <EyeIcon className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="flex-1"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleAISuggest(contact)
                                }}
                              >
                                <SparklesIcon className="w-4 h-4 mr-1" />
                                AI Suggest
                              </Button>
                            </div>
                          </Card>
                        </motion.div>
                      </Reorder.Item>
                    ))}
                  </Reorder.Group>
                  {stageContacts.length === 0 && (
                    <div className="h-24 rounded-2xl border-2 border-dashed border-metallic-200 flex items-center justify-center text-sm text-metallic-400">
                      Drop contacts here
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </motion.div>
      )}

      {/* List View */}
      {view === 'list' && (
        <motion.div variants={item}>
          <Card padding="sm">
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-metallic-500">{filteredContacts.length} contact{filteredContacts.length !== 1 ? 's' : ''} found</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-metallic-500 border-b border-metallic-100">
                    <th className="pb-3 font-medium cursor-pointer hover:text-metallic-700 select-none" onClick={() => handleSort('company')}>
                      <span className="inline-flex items-center gap-1">Company {sortField === 'company' && (sortDir === 'asc' ? '↑' : '↓')}</span>
                    </th>
                    <th className="pb-3 font-medium cursor-pointer hover:text-metallic-700 select-none" onClick={() => handleSort('contactPerson')}>
                      <span className="inline-flex items-center gap-1">Contact {sortField === 'contactPerson' && (sortDir === 'asc' ? '↑' : '↓')}</span>
                    </th>
                    <th className="pb-3 font-medium cursor-pointer hover:text-metallic-700 select-none" onClick={() => handleSort('value')}>
                      <span className="inline-flex items-center gap-1">Value {sortField === 'value' && (sortDir === 'asc' ? '↑' : '↓')}</span>
                    </th>
                    <th className="pb-3 font-medium cursor-pointer hover:text-metallic-700 select-none" onClick={() => handleSort('stage')}>
                      <span className="inline-flex items-center gap-1">Stage {sortField === 'stage' && (sortDir === 'asc' ? '↑' : '↓')}</span>
                    </th>
                    <th className="pb-3 font-medium">Tags</th>
                    <th className="pb-3 font-medium">Last Activity</th>
                    <th className="pb-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-metallic-100">
                  <AnimatePresence>
                    {filteredContacts.map((contact) => (
                      <motion.tr
                        key={contact.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="hover:bg-metallic-50 cursor-pointer"
                        onClick={() => openDetail(contact)}
                      >
                        <td className="py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-sm font-bold text-primary-700">
                              {contact.avatar}
                            </div>
                            <span className="font-medium text-metallic-900">{contact.company}</span>
                          </div>
                        </td>
                        <td className="py-4">
                          <p className="text-metallic-900">{contact.contactPerson}</p>
                          <p className="text-sm text-metallic-500">{contact.email}</p>
                        </td>
                        <td className="py-4 font-semibold text-primary-600">${contact.value.toLocaleString()}</td>
                        <td className="py-4">
                          <Badge variant={STAGE_BADGE_VARIANT[contact.stage]}>{getStageName(contact.stage)}</Badge>
                        </td>
                        <td className="py-4">
                          <div className="flex flex-wrap gap-1">
                            {contact.tags.slice(0, 2).map((tag) => (
                              <Badge key={tag} variant="info" size="sm">
                                {tag}
                              </Badge>
                            ))}
                            {contact.tags.length > 2 && (
                              <Badge variant="info" size="sm">+{contact.tags.length - 2}</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-4 text-metallic-500 text-sm whitespace-nowrap">{contact.lastActivity}</td>
                        <td className="py-4">
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" onClick={() => openDetail(contact)}>
                              <EyeIcon className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleAISuggest(contact)}>
                              <SparklesIcon className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm">
                              <PencilIcon className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="hover:text-error-500">
                              <TrashIcon className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
              {filteredContacts.length === 0 && (
                <div className="text-center py-12 text-metallic-400">
                  <MagnifyingGlassIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No contacts match your search criteria.</p>
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      )}

      {/* Create Contact Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} title="Create New Contact" size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Company Name"
              placeholder="Acme Corp"
              leftIcon={<BuildingOfficeIcon className="w-5 h-5" />}
              value={newContact.company}
              onChange={(e) => setNewContact((p) => ({ ...p, company: e.target.value }))}
            />
            <Input
              label="Contact Person"
              placeholder="John Doe"
              leftIcon={<UserGroupIcon className="w-5 h-5" />}
              value={newContact.contactPerson}
              onChange={(e) => setNewContact((p) => ({ ...p, contactPerson: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Email"
              type="email"
              placeholder="john@acme.com"
              leftIcon={<EnvelopeIcon className="w-5 h-5" />}
              value={newContact.email}
              onChange={(e) => setNewContact((p) => ({ ...p, email: e.target.value }))}
            />
            <Input
              label="Phone"
              type="tel"
              placeholder="+1 (555) 123-4567"
              leftIcon={<PhoneIcon className="w-5 h-5" />}
              value={newContact.phone}
              onChange={(e) => setNewContact((p) => ({ ...p, phone: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Deal Value"
              type="number"
              placeholder="50000"
              leftIcon={<span className="text-metallic-400 text-sm font-medium">$</span>}
              value={newContact.value}
              onChange={(e) => setNewContact((p) => ({ ...p, value: e.target.value }))}
            />
            <div>
              <label className="block text-sm font-medium text-metallic-700 mb-2">Stage</label>
              <select
                className="w-full px-4 py-3 bg-white border border-metallic-200 rounded-xl text-metallic-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                value={newContact.stage}
                onChange={(e) => setNewContact((p) => ({ ...p, stage: e.target.value }))}
              >
                {STAGES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <Input
            label="Tags"
            placeholder="Enterprise, SaaS, Q2 Target (comma-separated)"
            leftIcon={<TagIcon className="w-5 h-5" />}
            value={newContact.tags}
            onChange={(e) => setNewContact((p) => ({ ...p, tags: e.target.value }))}
          />
          <div>
            <label className="block text-sm font-medium text-metallic-700 mb-2">Notes</label>
            <textarea
              className="w-full px-4 py-3 bg-white border border-metallic-200 rounded-xl text-metallic-900 placeholder-metallic-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 min-h-[100px] resize-y"
              placeholder="Add any notes about this contact..."
              value={newContact.notes}
              onChange={(e) => setNewContact((p) => ({ ...p, notes: e.target.value }))}
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setIsCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button leftIcon={<PlusIcon className="w-5 h-5" />} onClick={handleCreateContact}>
              Create Contact
            </Button>
          </div>
        </div>
      </Modal>

      {/* Contact Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title={selectedContact?.company || ''}
        size="xl"
      >
        {selectedContact && (
          <div className="space-y-6">
            {/* Contact Header */}
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center text-xl font-bold text-primary-700 flex-shrink-0">
                {selectedContact.avatar}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-xl font-bold text-metallic-900">{selectedContact.company}</h3>
                <p className="text-metallic-500">{selectedContact.contactPerson}</p>
                <div className="flex items-center gap-4 mt-2 text-sm text-metallic-500">
                  <span className="flex items-center gap-1">
                    <EnvelopeIcon className="w-4 h-4" />
                    {selectedContact.email}
                  </span>
                  <span className="flex items-center gap-1">
                    <PhoneIcon className="w-4 h-4" />
                    {selectedContact.phone}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {selectedContact.tags.map((tag) => (
                    <Badge key={tag} variant="primary" size="sm">
                      <TagIcon className="w-3 h-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-2xl font-bold text-primary-600">${selectedContact.value.toLocaleString()}</div>
                <Badge variant={STAGE_BADGE_VARIANT[selectedContact.stage]} size="md" dot>
                  {getStageName(selectedContact.stage)}
                </Badge>
              </div>
            </div>

            {/* AI Suggest Button */}
            <Button
              variant="secondary"
              leftIcon={<SparklesIcon className="w-5 h-5" />}
              onClick={() => handleAISuggest(selectedContact)}
              className="w-full"
            >
              AI Sales Assistant — Get email draft, close score & next steps
            </Button>

            {/* Detail Tabs */}
            <div className="border-b border-metallic-100">
              <div className="flex gap-6">
                {[
                  { key: 'timeline' as const, label: 'Activity Timeline', icon: ClockIcon },
                  { key: 'notes' as const, label: 'Notes', icon: PencilIcon },
                  { key: 'history' as const, label: 'Stage History', icon: ArrowTrendingUpIcon },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setDetailTab(tab.key)}
                    className={clsx(
                      'flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors',
                      detailTab === tab.key ? 'border-primary-500 text-primary-600' : 'border-transparent text-metallic-500 hover:text-metallic-700'
                    )}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
              {detailTab === 'timeline' && (
                <motion.div key="timeline" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <div className="space-y-0">
                    {selectedContact.activities.map((activity, idx) => (
                      <div key={activity.id} className="flex gap-4 relative">
                        <div className="flex flex-col items-center">
                          <div className="w-9 h-9 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0 z-10">
                            <activity.icon className="w-4 h-4 text-primary-600" />
                          </div>
                          {idx < selectedContact.activities.length - 1 && (
                            <div className="w-px h-full bg-metallic-200 my-1" />
                          )}
                        </div>
                        <div className="pb-6 pt-1 flex-1">
                          <p className="text-sm text-metallic-900 font-medium">{activity.description}</p>
                          <p className="text-xs text-metallic-400 mt-1">{activity.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
              {detailTab === 'notes' && (
                <motion.div key="notes" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <div className="p-4 bg-metallic-50 rounded-xl">
                    <p className="text-sm text-metallic-700 leading-relaxed whitespace-pre-wrap">{selectedContact.notes}</p>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-metallic-700 mb-2">Add a note</label>
                    <textarea
                      className="w-full px-4 py-3 bg-white border border-metallic-200 rounded-xl text-metallic-900 placeholder-metallic-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 min-h-[80px] resize-y"
                      placeholder="Write a note..."
                    />
                    <div className="flex justify-end mt-2">
                      <Button size="sm">Save Note</Button>
                    </div>
                  </div>
                </motion.div>
              )}
              {detailTab === 'history' && (
                <motion.div key="history" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                  <div className="space-y-3">
                    {selectedContact.stageHistory.map((entry, idx) => {
                      const stage = STAGES.find((s) => s.id === entry.stage)
                      return (
                        <div key={idx} className="flex items-center gap-3">
                          <div className={clsx('w-3 h-3 rounded-full flex-shrink-0', stage?.dotColor || 'bg-metallic-400')} />
                          <div className="flex-1">
                            <span className="text-sm font-medium text-metallic-900">{stage?.name || entry.stage}</span>
                          </div>
                          <span className="text-sm text-metallic-500">{entry.date}</span>
                          {idx === selectedContact.stageHistory.length - 1 && (
                            <Badge variant={STAGE_BADGE_VARIANT[entry.stage]} size="sm">
                              Current
                            </Badge>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </Modal>

      {/* AI Suggestion Modal */}
      <Modal isOpen={aiModalOpen} onClose={() => setAiModalOpen(false)} title="AI Sales Assistant" size="lg">
        {aiLoading ? (
          <div className="flex flex-col items-center py-12 gap-4">
            <div className="relative">
              <SparklesIcon className="w-10 h-10 text-primary-500 animate-pulse" />
              <div className="absolute inset-0 w-10 h-10 border-2 border-primary-200 rounded-full animate-ping" />
            </div>
            <p className="text-metallic-500 text-sm">Analyzing contact and generating suggestions...</p>
          </div>
        ) : aiSuggestion ? (
          <div className="space-y-5">
            <div className="p-5 bg-gradient-to-r from-primary-50 to-purple-50 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-metallic-900 flex items-center gap-2">
                  <ArrowTrendingUpIcon className="w-5 h-5 text-primary-500" />
                  Close Probability
                </h4>
                <Badge variant={aiSuggestion.closeScore >= 70 ? 'success' : aiSuggestion.closeScore >= 40 ? 'warning' : 'error'}>
                  {aiSuggestion.closeScore}%
                </Badge>
              </div>
              <div className="w-full h-3 bg-white/60 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${aiSuggestion.closeScore}%` }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                  className={clsx(
                    'h-full rounded-full',
                    aiSuggestion.closeScore >= 70 ? 'bg-success-500' : aiSuggestion.closeScore >= 40 ? 'bg-warning-500' : 'bg-error-500'
                  )}
                />
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-metallic-900 mb-3 flex items-center gap-2">
                <EnvelopeIcon className="w-5 h-5 text-primary-500" />
                Suggested Follow-up Email
              </h4>
              <div className="p-4 bg-metallic-50 rounded-xl text-sm text-metallic-700 whitespace-pre-line leading-relaxed border border-metallic-100">
                {aiSuggestion.emailDraft}
              </div>
            </div>
            <div className="p-4 bg-primary-50 rounded-xl">
              <h4 className="font-semibold text-metallic-900 mb-2 flex items-center gap-2">
                <SparklesIcon className="w-5 h-5 text-primary-500" />
                Next Best Action
              </h4>
              <p className="text-sm text-metallic-600">{aiSuggestion.nextAction}</p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={() => setAiModalOpen(false)}>
                Close
              </Button>
              <Button leftIcon={<EnvelopeIcon className="w-4 h-4" />}>Send Email</Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </motion.div>
  )
}
